/**
 * useAIClassifySession · AI 智能分类的会话管理 hook
 *
 * 协议重定位 (2026-05 重构):
 *   1. 团队级预热: groupID 变化时调一次 init 拿 recommended_prompts / note;
 *      不再创建/恢复会话
 *   2. 会话恢复: 进入页面有 lastConvId 时拉一次 history 恢复消息气泡
 *   3. 新对话: 点"新对话"是纯前端动作 — 把 redux conversationId 置 null + 切到欢迎页;
 *      用户发首条消息时 send 接口 conversation_id 传空, 后端落库新会话并通过 SSE done
 *      事件返回 id, 由 buildStreamHandlers 触发 conversationReplaced 写回 redux
 *   4. localStorage 同步: 用一个 effect 订阅 redux conversationId, 自动写 / 清 lastConvId,
 *      所有 dispatch 端不再手动调 saveLastConvId / clearLastConvId
 *
 * 流式回调定义在 buildStreamHandlers.ts, 会话恢复 / 团队预热逻辑在 conversationRestore.ts。
 */

import { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  conversationOpened,
  bumpNewConvAnim,
  messageAppended,
  messagesTruncatedFrom,
  conversationCleared,
  teamSwitched,
  selectAllMessages,
  selectComposerFolderContext,
  selectConversationId,
  selectSessionStatus,
  streamStarted
} from '../../../slices/aiClassify'
import type { AssistantMessage, TextMessage } from '../../../types/ai-classify'
import { streamChat } from '../services/aiClassifySse'
import { buildStreamHandlers } from '../services/buildStreamHandlers'
import {
  prewarmTeam,
  loadRecommendCards,
  restoreLastConversation
} from '../services/conversationRestore'

/** 本地生成的消息 id · 时间戳 + 6 位随机串 */
function genLocalMessageId(prefix: string): string {
  return `ai-cls-${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
}

/**
 * conversation_id 持久化, 用户重新打开抽屉时恢复上次对话。
 * key 带 groupID 维度, 切换团队时不会互相混淆。
 *
 * 写入 / 清除时机由 hook 内的"localStorage 同步 effect"统一管理 (单向跟随 redux conversationId),
 * 业务代码 (startNewConversation / switchConversation / SSE done 回填) 不直接调 save / clear。
 */
const LAST_CONV_KEY_PREFIX = 'ai_classify_last_conv_id__'

function loadLastConvId(groupID: string): string {
  if (typeof window === 'undefined' || !groupID) return ''
  try {
    return window.localStorage.getItem(LAST_CONV_KEY_PREFIX + groupID) || ''
  } catch {
    return ''
  }
}

function saveLastConvId(groupID: string, convId: string): void {
  if (typeof window === 'undefined' || !groupID) return
  try {
    window.localStorage.setItem(LAST_CONV_KEY_PREFIX + groupID, convId)
  } catch {
    /* silent */
  }
}

function clearLastConvId(groupID: string): void {
  if (typeof window === 'undefined' || !groupID) return
  try {
    window.localStorage.removeItem(LAST_CONV_KEY_PREFIX + groupID)
  } catch {
    /* silent */
  }
}

export interface UseAIClassifySessionApi {
  /** 发送一条用户消息, Composer 输入框和推荐提示词点击都走这里 */
  sendUserMessage: (text: string) => void
  /**
   * 重新发起某条 user 消息所在的一轮对话 (MessageText hover「刷新」按钮触发)。
   * 移除该消息及之后的所有消息, 再用相同文案重新发送。streaming 期间不允许调用。
   */
  regenerateFromUserMessage: (messageId: string, text: string) => void
  /**
   * 开新对话 (右上角「新对话」按钮触发) · 纯前端动作, 不调任何接口。
   * 切到欢迎页 + 播一次 slide-up 动画; 用户在欢迎页发首条消息时 send 自动建会话。
   */
  startNewConversation: () => void
  /**
   * 切换到历史会话 (历史会话列表中点击某条时触发)。
   * 写入 redux conversationId + 拉 history 恢复消息气泡, 不调 init 接口。
   */
  switchConversation: (targetConvId: string) => void
}

export function useAIClassifySession(): UseAIClassifySessionApi {
  const dispatch = useAppDispatch()
  const conversationId = useAppSelector(selectConversationId)
  const sessionStatus = useAppSelector(selectSessionStatus)
  const messages = useAppSelector(selectAllMessages)
  // groupID 取自 RTK group 树（pojo 直读）
  const groupID = useAppSelector((s) => s.group.groupId)
  // 团队名称, 注入 SYSTEM NOTE 让 LLM 知道当前团队
  const groupName = useAppSelector((s) => s.group.groupName || '')
  // Composer 顶部「当前文件夹」上下文 · 详情页有值、列表页 / 点 X 移除后为 null
  // send 时带 parent_folder_id：有文件夹上下文 → 文件夹 ID，否则 → 空串
  const composerFolderContext = useAppSelector(selectComposerFolderContext)

  /** 当前进行中的 SSE 请求的 AbortController, 卸载或切换团队/会话时取消请求 */
  const abortRef = useRef<AbortController | null>(null)
  /** 已处理的 user 消息 id 集合, 防止同一条消息重复触发 SSE 请求 */
  const processedRef = useRef<Set<string>>(new Set())
  /**
   * messagesRef · 在异步回调里读最新 messages 状态
   *
   * 用途: restoreLastConversation 拉到结果时, 检查 store 中是否已有本地消息。
   * 如果有, 说明用户已发起新一轮对话, 历史数据会和本地消息重复, 应跳过。
   * 用 ref 而非闭包: 闭包捕获的是 effect 起跑时的空数组, 异步期间的变化拿不到。
   */
  const messagesRef = useRef(messages)
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // ─────────────────────────────────────────────────────────────────────────
  // Effect 1: 团队预热 + 上次会话恢复
  //
  // 触发时机:
  //   · 首次 mount (groupID 从 '' 变到真实值)
  //   · 切换团队 (groupID 从 A 变到 B)
  //
  // 行为:
  //   1. dispatch(teamSwitched()) 把 sessionSlice / messagesSlice / selectionSlice 等
  //      所有切片清回初始态, 防止上一个团队的状态串台到新团队
  //   2. processedRef 清空 (这是 ref 不归 redux 管, 单独清)
  //   3. 并发跑: 团队预热 (拉 init) + 首页推荐卡 (拉 v2 init) + 上次会话恢复
  //      (有 lastConvId 时拉 history)
  //   4. cleanup 时 abort 当前 SSE + 上面两个并发请求
  //
  // 注意: useAIClassifySession 在 AIClassifyPage 顶层挂载, 几乎不会卸载;
  //       groupID 切换是主要触发场景。
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!groupID) return

    // 切团队 / 首次进入 → 整切片清初始; 首次 mount 时 state 已是初始值, dispatch 无副作用
    dispatch(teamSwitched())
    processedRef.current = new Set()

    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }

    const ctrl = new AbortController()

    // 团队级预热 (失败已在 prewarmTeam 内部兜底, 不会向上抛)
    void prewarmTeam(dispatch, groupID, ctrl.signal)

    // 首页推荐卡 (v2 init) · 与预热并行、互不依赖; 失败已在内部兜底
    void loadRecommendCards(dispatch, groupID, ctrl.signal)

    // 上次会话恢复: 有 lastConvId 才拉 history; 没有就保持欢迎页
    const lastConvId = loadLastConvId(groupID)
    if (lastConvId) {
      dispatch(conversationOpened(lastConvId))
      restoreLastConversation(
        dispatch,
        lastConvId,
        groupID,
        processedRef,
        messagesRef
      ).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[ai-classify] restore last conversation failed:', err)
      })
    }

    return () => ctrl.abort()
  }, [groupID, dispatch])

  // ─────────────────────────────────────────────────────────────────────────
  // Effect 2: localStorage 同步
  //
  // redux conversationId 与 localStorage 的 lastConvId 单向绑定:
  //   · 有值 (SSE done 回填 / switchConversation 写入) → 写 lastConvId
  //   · 为 null (startNewConversation / teamSwitched 清空) → 清 lastConvId
  //
  // 这样所有改变 conversationId 的路径都不需要手动管 localStorage,
  // 避免遗漏导致两边不一致。
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!groupID) return
    if (conversationId) saveLastConvId(groupID, conversationId)
    else clearLastConvId(groupID)
  }, [conversationId, groupID])

  /**
   * 主动发送消息入口, Composer 输入框提交时调用。
   * 写入一条 user 类型消息到 store, SSE effect 会自动检测到并发起请求。
   */
  const sendUserMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const msg: TextMessage = {
        id: genLocalMessageId('msg'),
        kind: 'text',
        role: 'user',
        content: trimmed,
        createdAt: Date.now()
      }
      dispatch(messageAppended(msg))
    },
    [dispatch]
  )

  /**
   * 重新发起某条 user 消息所在的一轮对话 (MessageText hover「刷新」按钮触发)。
   * 从 messageId 这条 user 消息开始, 移除它及之后的所有消息 (含 AI 回复、工具卡片等),
   * 再用相同文案发一条新的 user 消息, 走与 Composer 相同的 SSE 发起链路。
   *
   * · sessionStatus === 'streaming' 时直接返回, 不允许并发发送
   * · 若 abortRef 上仍绑定着 AbortController, 先取消请求并清空
   * · 新 user 消息会有新 id, 自然触发 SSE effect 中"未处理的 user 消息 → 发起 SSE 请求"的分支
   */
  const regenerateFromUserMessage = useCallback(
    (messageId: string, text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      if (sessionStatus === 'streaming') return
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      dispatch(messagesTruncatedFrom({ messageId }))
      // 用 micro-task 让 truncate 的 dispatch 先执行完毕, 再追加新 user 消息,
      // 避免同一帧内 SSE effect 读到截断前的中间状态
      Promise.resolve().then(() => sendUserMessage(trimmed))
    },
    [dispatch, sendUserMessage, sessionStatus]
  )

  // ─────────────────────────────────────────────────────────────────────────
  // Effect 3: SSE 发起
  //
  // 监听 messages 变化: 如果最新的 user 文本消息尚未处理, 则发起一轮 SSE 请求。
  // conversationId 为 null (用户在欢迎页发首条消息) 时, 请求 body 的 conversation_id
  // 传空字符串 → 后端落库新会话并通过 done 事件返回 id, buildStreamHandlers.onDone
  // dispatch conversationReplaced 写回 redux (reducer 内严格守卫防废弃 SSE 污染)。
  //
  // 防止重复触发的两层防护:
  //   1. processedRef —— 同一次 mount 期间, 同一条消息不会重复发起请求
  //   2. 列表态判断 —— lastUserText 之后已有 assistant 消息说明已处理过;
  //      用于跨 mount 场景 (切 tab 回来时 processedRef 是新的空集合,
  //      但 messages 在 Redux 中持久保留, 不做此判断会重复发送给后端)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (sessionStatus === 'streaming') return

    let lastUserTextIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.kind === 'text' && m.role === 'user') {
        lastUserTextIdx = i
        break
      }
    }
    if (lastUserTextIdx < 0) return

    // lastUserText 之后已有 assistant 消息, 说明这条消息已经处理过, 不再重复发起
    for (let i = lastUserTextIdx + 1; i < messages.length; i++) {
      if (messages[i].kind === 'assistant') return
    }

    const lastUserText = messages[lastUserTextIdx] as TextMessage
    if (processedRef.current.has(lastUserText.id)) return
    processedRef.current.add(lastUserText.id)

    // 创建一条空的 assistant 消息作为流式内容的容器 (status=streaming)
    const assistantId = genLocalMessageId('asst')
    const shell: AssistantMessage = {
      id: assistantId,
      kind: 'assistant',
      role: 'ai',
      status: 'streaming',
      text: '',
      createdAt: Date.now()
    }
    dispatch(messageAppended(shell))
    dispatch(streamStarted())

    const ctrl = new AbortController()
    abortRef.current = ctrl

    void streamChat(
      {
        // 新对话欢迎页发首条 → conversationId 为 null → 传空字符串让后端创建并回填
        conversation_id: conversationId ?? '',
        message: lastUserText.content,
        group_id: groupID,
        group_name: groupName,
        // 文件夹详情页 → 当前文件夹 ID(number)；列表页 / 已移除上下文 → 0（最外层）
        parent_folder_id: Number(composerFolderContext?.folderId) || 0
      },
      buildStreamHandlers({
        dispatch,
        assistantId,
        userText: lastUserText.content,
        groupID
      }),
      { signal: ctrl.signal }
    )
    // streamChat resolve 后 abortRef 已无意义, 不做清理也没关系
  }, [
    messages,
    conversationId,
    sessionStatus,
    dispatch,
    groupID,
    groupName,
    composerFolderContext
  ])

  // 组件卸载时取消进行中的 SSE 请求
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  /**
   * 开新对话 (右上角「新对话」按钮触发) · 纯前端动作, 不调任何接口。
   *
   * · 已经在欢迎页 (conversationId 为 null) → 只播一次动画, state 不动
   * · 已有会话 → abort 进行中 SSE + dispatch conversationCleared 切回欢迎页
   *
   * localStorage 由 Effect 2 自动清除, 这里不手动调 clearLastConvId。
   * 用户在新欢迎页发首条消息时, send 接口 conversation_id 传空, 后端落库新会话
   * 并通过 done 事件回填新 id, 没有"主动创建空白会话"的接口请求。
   */
  const startNewConversation = useCallback(() => {
    if (!conversationId) {
      dispatch(bumpNewConvAnim())
      return
    }
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    processedRef.current = new Set()
    dispatch(conversationCleared())
    dispatch(bumpNewConvAnim())
  }, [conversationId, dispatch])

  /**
   * 切换到历史会话 (历史会话列表中点击某条时触发)。
   *
   * abort 当前 SSE → dispatch conversationCleared 清场 → conversationOpened 写新 id →
   * restoreLastConversation 拉 history 恢复消息气泡。
   *
   * 不调 init: 协议重定位后 init 仅做团队预热, 切会话不需要重新预热;
   * 维度链预览恢复另行处理 (A4 降级 · 等待历史回放方案)。
   *
   * localStorage 由 Effect 2 自动写入。
   */
  const switchConversation = useCallback(
    (targetConvId: string) => {
      if (!targetConvId) return
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      processedRef.current = new Set()
      dispatch(conversationCleared())
      dispatch(conversationOpened(targetConvId))

      if (!groupID) return

      restoreLastConversation(
        dispatch,
        targetConvId,
        groupID,
        processedRef
      ).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[ai-classify] switch history load failed:', err)
      })
    },
    [dispatch, groupID]
  )

  return {
    sendUserMessage,
    regenerateFromUserMessage,
    startNewConversation,
    switchConversation
  }
}
